import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, isValid, parseISO } from "date-fns";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function generateRandomPasswordBase64(length = 6): string {
	const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let password = "";
	for (let i = 0; i < length; i++) {
		password += chars.charAt(Math.floor(Math.random() * chars.length));
	}

	return btoa(password);
}

export function deepTrimStrings(obj: any, excludeKeys: string[] = []): any {
	if (typeof obj === "string") return obj.trim();
	if (typeof obj !== "object" || obj === null) return obj;
	if (Array.isArray(obj)) return obj.map((item) => deepTrimStrings(item, excludeKeys));

	return Object.fromEntries(
		Object.entries(obj).map(([key, value]) => {
			if (excludeKeys.includes(key)) return [key, value];
			return [key, deepTrimStrings(value, excludeKeys)];
		}),
	);
}

export function formatDate(
	date: Date | number | string | null | undefined,
	dateFormat = "yyyy-MM-dd HH:mm:ss",
	fallback = "",
): string {
	// Handle null/undefined cases first
	if (date == null || date === "") {
		return fallback;
	}

	try {
		let dateObj: Date;

		if (typeof date === "string") {
			// Try parsing ISO string first, then fall back to Date constructor
			dateObj = parseISO(date);
			if (!isValid(dateObj)) {
				dateObj = new Date(date);
			}
		} else if (typeof date === "number") {
			dateObj = new Date(date);
		} else {
			dateObj = date;
		}

		if (!isValid(dateObj)) {
			console.warn("formatDate: Invalid date provided", date);
			return fallback;
		}

		return format(dateObj, dateFormat);
	} catch (error) {
		console.error("formatDate error:", error);
		return fallback;
	}
}

export function getGMTLabel() {
	const date = new Date();

	const offsetMinutes = date.getTimezoneOffset();
	const offsetHours = -offsetMinutes / 60;
	return offsetHours >= 0 ? `GMT+${offsetHours}` : `GMT${offsetHours}`;
}

export const GTMLabel = getGMTLabel();

export const formatNumber = (value: number | string): string => {
	const stringValue = value.toString();

	const [integerPart, decimalPart = ""] = stringValue.split(".");

	const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

	return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};
